import { AlertCircle, RefreshCw } from 'lucide-react';

import React from 'react';

import { Alert, Button } from '@splitfiesta/ui';

import { addBreadcrumb, isValidationError } from '@/lib/errors';

import { ErrorBoundary } from './ErrorBoundary';

interface FormErrorBoundaryProps {
  children: React.ReactNode;
  onReset?: () => void;
  /**
   * Form name for tracking (e.g., 'ExpenseForm', 'GroupForm')
   */
  formName?: string;
}

/**
 * Error boundary specifically for form components
 * Provides a more form-appropriate error UI
 *
 * Enhanced with:
 * - Validation error detection
 * - Form-specific breadcrumb tracking
 */
export function FormErrorBoundary({
  children,
  onReset,
  formName,
}: FormErrorBoundaryProps) {
  const formErrorFallback = (
    error: Error,
    _errorInfo: React.ErrorInfo,
    reset: () => void
  ) => {
    const handleReset = () => {
      // Add breadcrumb for form reset
      addBreadcrumb({
        category: 'user_action',
        message: `User reset ${formName || 'form'} after error`,
        level: 'info',
        data: {
          formName,
        },
      });

      if (onReset) {
        onReset();
      }
      reset();
    };

    // Check if it's a validation error
    const isValidation = isValidationError(error);

    return (
      <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-xs">
        <div className="flex items-start gap-3 mb-4">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              {isValidation ? 'Validation Error' : 'Form Error'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isValidation
                ? 'There was a problem validating your input. Please check the form and try again.'
                : 'Something went wrong while processing your form. Your data has not been lost.'}
            </p>
          </div>
        </div>

        {import.meta.env.DEV && (
          <Alert type="error" message={error.message} className="mb-4" />
        )}

        <div className="flex gap-3">
          <Button
            variant="primary"
            size="sm"
            onClick={handleReset}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Reset Form
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </Button>
        </div>
      </div>
    );
  };

  return (
    <ErrorBoundary
      boundaryName={`FormErrorBoundary:${formName || 'unknown'}`}
      fallback={formErrorFallback}
    >
      {children}
    </ErrorBoundary>
  );
}

export default FormErrorBoundary;
